import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  ImagePlus,
  Loader2,
  Plus,
  Save,
  Star,
  Trash2,
} from 'lucide-react';
import { supabase } from '../../../../Supabase/SupabaseClient';
import { convertToWebp, formatBytes, uploadImageToBucket } from '../../../../utils/imageUpload';
import {
  CATEGORIAS,
  agruparPorCategoria,
  formatPrice,
  resolverSlug,
} from '../../../../config/catalogoServicios';
import styles from './Servicios.module.css';

const BUCKET = 'fotos';

const EMPTY_DRAFT = {
  nombre: '',
  categoria: 'eventos',
  slug: '',
  nota: '',
  opciones: '',
  orden: 0,
  activo: true,
  precio: '',
  descripcion: '',
};

const draftFromRow = (fila) => ({
  nombre: fila.nombre || '',
  categoria: fila.categoria || 'eventos',
  slug: fila.slug || '',
  nota: fila.nota || '',
  opciones: Array.isArray(fila.opciones) ? fila.opciones.join(', ') : '',
  orden: fila.orden ?? 0,
  activo: fila.activo !== false,
  precio:
    fila.precio !== null && fila.precio !== undefined ? String(fila.precio) : '',
  descripcion: fila.descripcion || '',
});

function Servicios() {
  const [servicios, setServicios] = useState([]);
  const [fotos, setFotos] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [summary, setSummary] = useState(null);
  const fileInputRef = useRef(null);

  const loadServicios = useCallback(async () => {
    const { data, error: serviciosError } = await supabase
      .from('servicios')
      .select('*')
      .order('categoria', { ascending: true })
      .order('orden', { ascending: true });

    if (serviciosError) throw serviciosError;
    setServicios(data || []);
  }, []);

  const loadFotos = useCallback(async () => {
    const { data, error: fotosError } = await supabase
      .from('servicio_fotos')
      .select('*')
      .order('orden', { ascending: true });

    if (fotosError) throw fotosError;
    setFotos(data || []);
  }, []);

  useEffect(() => {
    let active = true;

    const init = async () => {
      setLoading(true);
      setError(null);
      try {
        await loadServicios();
      } catch (err) {
        if (active) setError(err.message || 'No se pudieron cargar los servicios.');
      }
      try {
        await loadFotos();
      } catch (err) {
        if (active) {
          setError(
            'No se pudieron cargar las fotos. Revisá que la tabla servicio_fotos exista en Supabase.'
          );
        }
      }
      if (active) setLoading(false);
    };

    init();
    return () => {
      active = false;
    };
  }, [loadServicios, loadFotos]);

  const grupos = useMemo(() => agruparPorCategoria(servicios), [servicios]);

  const fotosPorServicio = useMemo(() => {
    const groups = new Map();
    fotos.forEach((foto) => {
      const lista = groups.get(foto.servicio_id) || [];
      lista.push(foto);
      groups.set(foto.servicio_id, lista);
    });
    return groups;
  }, [fotos]);

  const seleccion = useMemo(
    () => servicios.find((s) => s.id === selectedId) || null,
    [servicios, selectedId]
  );

  const selectedFotos = useMemo(() => {
    if (selectedId === null || selectedId === undefined) return [];
    return fotosPorServicio.get(selectedId) || [];
  }, [fotosPorServicio, selectedId]);

  const configurados = servicios.filter(
    (s) => s.precio !== null && s.precio !== undefined && s.precio !== ''
  ).length;

  const coverFor = (fila) => {
    if (!fila) return null;
    const lista = fotosPorServicio.get(fila.id) || [];
    const foto = lista.find((item) => item.destacada) || lista[0];
    if (foto) return foto;
    if (fila.imagen_url) return { url_foto: fila.imagen_url };
    return null;
  };

  const pick = (fila) => {
    setSelectedId(fila.id);
    setDraft(draftFromRow(fila));
    setError(null);
    setSuccess(null);
    setSummary(null);
  };

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    setSuccess(null);

    const nombre = (draft.nombre || '').trim() || 'Nuevo servicio';
    const slug = (draft.slug || '').trim() || resolverSlug(nombre);

    try {
      const { data, error: insertError } = await supabase
        .from('servicios')
        .insert({
          nombre,
          slug,
          categoria: draft.categoria,
          nota: draft.nota.trim() || null,
          opciones: draft.opciones
            .split(',')
            .map((o) => o.trim())
            .filter(Boolean),
          orden: Number(draft.orden) || 0,
          activo: draft.activo,
          precio: draft.precio.trim() === '' ? null : Number(draft.precio),
          descripcion: draft.descripcion,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      await loadServicios();
      setSelectedId(data.id);
      setDraft(draftFromRow(data));
      setSuccess(`"${nombre}" fue creado.`);
    } catch (err) {
      setError(err.message || 'No se pudo crear el servicio.');
    } finally {
      setCreating(false);
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!seleccion) return;

    const nombre = draft.nombre.trim();
    if (!nombre) {
      setError('El nombre no puede quedar vacío.');
      return;
    }

    const precioRaw = draft.precio.trim();
    const precio = precioRaw === '' ? null : Number(precioRaw);
    if (precio !== null && Number.isNaN(precio)) {
      setError('El precio debe ser un número.');
      return;
    }

    const opciones = draft.opciones
      .split(',')
      .map((o) => o.trim())
      .filter(Boolean);

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const { error: updateError } = await supabase
        .from('servicios')
        .update({
          nombre,
          slug: draft.slug.trim() || resolverSlug(nombre),
          categoria: draft.categoria,
          nota: draft.nota.trim() || null,
          opciones: opciones.length ? opciones : null,
          orden: Number(draft.orden) || 0,
          activo: draft.activo,
          precio,
          descripcion: draft.descripcion,
        })
        .eq('id', seleccion.id);

      if (updateError) throw updateError;

      await loadServicios();
      setSuccess('Cambios guardados.');
    } catch (err) {
      setError(err.message || 'No se pudo guardar el servicio.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!seleccion) return;
    const confirmado = window.confirm(
      `¿Eliminar "${seleccion.nombre}"? Se borran también sus fotos. Las órdenes y carritos existentes no se tocan.`
    );
    if (!confirmado) return;

    setRemoving(true);
    setError(null);
    setSuccess(null);

    try {
      const paths = selectedFotos.map((f) => f.path).filter(Boolean);
      if (paths.length) {
        await supabase.storage.from(BUCKET).remove(paths);
      }

      const { error: fotosError } = await supabase
        .from('servicio_fotos')
        .delete()
        .eq('servicio_id', seleccion.id);
      if (fotosError) throw fotosError;

      const { error: deleteError } = await supabase
        .from('servicios')
        .delete()
        .eq('id', seleccion.id);
      if (deleteError) throw deleteError;

      await Promise.all([loadServicios(), loadFotos()]);
      setSelectedId(null);
      setDraft(EMPTY_DRAFT);
      setSuccess(`"${seleccion.nombre}" fue eliminado.`);
    } catch (err) {
      setError(err.message || 'No se pudo eliminar el servicio.');
    } finally {
      setRemoving(false);
    }
  };

  const handleFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length || !seleccion) return;

    setUploading(true);
    setError(null);
    setSuccess(null);
    setSummary(null);

    const uploadedPaths = [];
    const convertedFiles = [];
    const newRows = [];

    try {
      let nextOrden = selectedFotos.length;

      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        const converted = await convertToWebp(file);
        convertedFiles.push(converted);
        const fileName = `${Date.now()}-${i}-${converted.fileName}`;
        const path = `servicios/${seleccion.id}/${fileName}`;

        const url = await uploadImageToBucket(BUCKET, path, converted.blob);
        uploadedPaths.push(path);

        newRows.push({
          servicio_id: seleccion.id,
          url_foto: url,
          path,
          orden: nextOrden,
          destacada: false,
          descripcion: '',
        });

        nextOrden += 1;
      }

      const { error: insertError } = await supabase
        .from('servicio_fotos')
        .insert(newRows);

      if (insertError) throw insertError;

      await loadFotos();
      setSummary({
        count: convertedFiles.length,
        before: convertedFiles.reduce((sum, item) => sum + item.originalBytes, 0),
        after: convertedFiles.reduce((sum, item) => sum + item.bytes, 0),
      });
      setSuccess(
        `${convertedFiles.length} ${
          convertedFiles.length === 1 ? 'foto subida' : 'fotos subidas'
        } en WebP.`
      );
    } catch (err) {
      if (uploadedPaths.length) {
        await Promise.allSettled(
          uploadedPaths.map((item) => supabase.storage.from(BUCKET).remove([item]))
        );
      }
      setError(err.message || 'No se pudieron subir las fotos.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteFoto = async (foto) => {
    setError(null);
    setSuccess(null);

    try {
      if (foto.path) {
        const { error: storageError } = await supabase.storage
          .from(BUCKET)
          .remove([foto.path]);
        if (storageError) throw storageError;
      }

      const { error: deleteError } = await supabase
        .from('servicio_fotos')
        .delete()
        .eq('id', foto.id);

      if (deleteError) throw deleteError;

      await loadFotos();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar la foto.');
    }
  };

  const handleTogglePrincipal = async (foto) => {
    setError(null);
    setSuccess(null);

    try {
      const { error: resetError } = await supabase
        .from('servicio_fotos')
        .update({ destacada: false })
        .eq('servicio_id', foto.servicio_id);

      if (resetError) throw resetError;

      if (!foto.destacada) {
        const { error: setDestacadaError } = await supabase
          .from('servicio_fotos')
          .update({ destacada: true })
          .eq('id', foto.id);

        if (setDestacadaError) throw setDestacadaError;
      }

      await loadFotos();
    } catch (err) {
      setError(err.message || 'No se pudo actualizar la portada.');
    }
  };

  const handleMove = async (foto, direction) => {
    const index = selectedFotos.findIndex((item) => item.id === foto.id);
    const targetIndex = index + direction;
    if (index < 0 || targetIndex < 0 || targetIndex >= selectedFotos.length) return;

    setError(null);
    setSuccess(null);

    const current = selectedFotos[index];
    const target = selectedFotos[targetIndex];

    try {
      const { error: firstError } = await supabase
        .from('servicio_fotos')
        .update({ orden: target.orden })
        .eq('id', current.id);

      if (firstError) throw firstError;

      const { error: secondError } = await supabase
        .from('servicio_fotos')
        .update({ orden: current.orden })
        .eq('id', target.id);

      if (secondError) throw secondError;

      await loadFotos();
    } catch (err) {
      setError(err.message || 'No se pudo reordenar la foto.');
    }
  };

  const renderItemButton = (fila) => {
    const cover = coverFor(fila);
    const imagen = CATEGORIAS.find((c) => c.id === fila.categoria)?.imagen;

    return (
      <button
        type="button"
        key={fila.id}
        className={`${styles.listItem} ${selectedId === fila.id ? styles.listItemActive : ''}`}
        onClick={() => pick(fila)}
        aria-current={selectedId === fila.id ? 'true' : undefined}
      >
        <span className={styles.thumb}>
          {cover ? (
            <img src={cover.url_foto} alt="" loading="lazy" />
          ) : imagen ? (
            <img src={imagen} alt="" loading="lazy" />
          ) : null}
        </span>
        <span className={styles.listMeta}>
          <span className={styles.listName}>{fila.nombre}</span>
          <span className={styles.listSub}>
            {fila.precio !== null && fila.precio !== undefined && fila.precio !== ''
              ? formatPrice(fila.precio)
              : 'Sin precio'}
            {fila.activo === false ? ' · oculto' : ''}
          </span>
          {fila.nota && <span className={styles.listNote}>{fila.nota}</span>}
          {Array.isArray(fila.opciones) && fila.opciones.length > 0 && (
            <span className={styles.listOptions}>{fila.opciones.join(' · ')}</span>
          )}
        </span>
      </button>
    );
  };

  if (loading) {
    return (
      <div className={styles.loading} role="status">
        <Loader2 className={styles.spinner} size={22} aria-hidden="true" />
        Cargando servicios…
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.overview}>
        <strong>{servicios.length}</strong> servicios ·{' '}
        <strong>{configurados}</strong> con precio cargado
      </div>

      {error && (
        <div className={styles.errorBox} role="alert">
          <AlertTriangle size={18} aria-hidden="true" />
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div className={styles.successBox} role="status">
          <Check size={18} aria-hidden="true" />
          <div>
            <p>{success}</p>
            {summary && summary.count > 0 && (
              <p className={styles.summary}>
                {formatBytes(summary.before)} → {formatBytes(summary.after)} de peso
              </p>
            )}
          </div>
        </div>
      )}

      <div className={styles.layout}>
        <aside className={styles.list} aria-label="Servicios">
          <button
            type="button"
            className={styles.newRowButton}
            onClick={handleCreate}
            disabled={creating}
          >
            {creating ? (
              <Loader2 className={styles.spinner} size={16} aria-hidden="true" />
            ) : (
              <Plus size={16} aria-hidden="true" />
            )}
            {creating ? 'Creando…' : 'Nuevo servicio'}
          </button>

          {grupos.map((grupo) => (
            <section key={grupo.id} className={styles.group}>
              <h3 className={styles.groupTitle}>{grupo.titulo}</h3>
              {grupo.items.map(renderItemButton)}
            </section>
          ))}

          {servicios.length === 0 && (
            <p className={styles.photosEmpty}>
              Todavía no hay servicios cargados.
            </p>
          )}
        </aside>

        {seleccion ? (
          <section className={styles.editor} aria-label={`Editar ${seleccion.nombre}`}>
            <header className={styles.editorHeader}>
              <div>
                <span className={styles.editorCategory}>
                  {CATEGORIAS.find((c) => c.id === draft.categoria)?.titulo ||
                    draft.categoria}
                </span>
                <h2 className={styles.editorTitle}>{seleccion.nombre}</h2>
                <p className={styles.editorHint}>
                  Se publica en /servicios/{draft.slug || resolverSlug(draft.nombre)}
                </p>
              </div>
            </header>

            <form className={styles.form} onSubmit={handleSave}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="servicio-nombre">
                  Nombre
                </label>
                <input
                  id="servicio-nombre"
                  className={styles.input}
                  type="text"
                  value={draft.nombre}
                  onChange={(e) => setDraft((d) => ({ ...d, nombre: e.target.value }))}
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="servicio-precio">
                  Precio (USD)
                </label>
                <input
                  id="servicio-precio"
                  className={styles.input}
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  placeholder="0"
                  value={draft.precio}
                  onChange={(e) => setDraft((d) => ({ ...d, precio: e.target.value }))}
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="servicio-categoria">
                  Categoría
                </label>
                <select
                  id="servicio-categoria"
                  className={styles.select}
                  value={draft.categoria}
                  onChange={(e) => setDraft((d) => ({ ...d, categoria: e.target.value }))}
                >
                  {CATEGORIAS.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.titulo}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="servicio-orden">
                  Orden
                </label>
                <input
                  id="servicio-orden"
                  className={styles.input}
                  type="number"
                  min="0"
                  step="1"
                  value={draft.orden}
                  onChange={(e) => setDraft((d) => ({ ...d, orden: e.target.value }))}
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="servicio-slug">
                  Slug (URL)
                </label>
                <input
                  id="servicio-slug"
                  className={styles.input}
                  type="text"
                  placeholder={resolverSlug(draft.nombre)}
                  value={draft.slug}
                  onChange={(e) => setDraft((d) => ({ ...d, slug: e.target.value }))}
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="servicio-nota">
                  Nota corta
                </label>
                <input
                  id="servicio-nota"
                  className={styles.input}
                  type="text"
                  placeholder="Estilo libre"
                  value={draft.nota}
                  onChange={(e) => setDraft((d) => ({ ...d, nota: e.target.value }))}
                />
              </div>

              <div className={`${styles.field} ${styles.fieldFull}`}>
                <label className={styles.label} htmlFor="servicio-opciones">
                  Opciones (separadas por coma)
                </label>
                <input
                  id="servicio-opciones"
                  className={styles.input}
                  type="text"
                  placeholder="2 máquinas, 4 máquinas"
                  value={draft.opciones}
                  onChange={(e) => setDraft((d) => ({ ...d, opciones: e.target.value }))}
                />
              </div>

              <div className={`${styles.field} ${styles.fieldFull}`}>
                <label className={styles.label} htmlFor="servicio-descripcion">
                  Descripción
                </label>
                <textarea
                  id="servicio-descripcion"
                  className={styles.textarea}
                  rows={5}
                  placeholder="Contale al cliente qué incluye este servicio…"
                  value={draft.descripcion}
                  onChange={(e) => setDraft((d) => ({ ...d, descripcion: e.target.value }))}
                />
              </div>

              <div className={`${styles.field} ${styles.fieldFull} ${styles.toggleField}`}>
                <input
                  id="servicio-activo"
                  type="checkbox"
                  checked={draft.activo}
                  onChange={(e) => setDraft((d) => ({ ...d, activo: e.target.checked }))}
                />
                <label className={styles.label} htmlFor="servicio-activo">
                  {draft.activo ? 'Visible en el sitio' : 'Oculto en el sitio'}
                </label>
              </div>

              <div className={styles.formActions}>
                <button type="submit" className={styles.saveButton} disabled={saving}>
                  {saving ? (
                    <Loader2 className={styles.spinner} size={18} aria-hidden="true" />
                  ) : (
                    <Save size={18} aria-hidden="true" />
                  )}
                  {saving ? 'Guardando…' : 'Guardar cambios'}
                </button>

                <button
                  type="button"
                  className={styles.dangerButton}
                  onClick={handleDelete}
                  disabled={removing}
                >
                  {removing ? (
                    <Loader2 className={styles.spinner} size={18} aria-hidden="true" />
                  ) : (
                    <Trash2 size={18} aria-hidden="true" />
                  )}
                  {removing ? 'Eliminando…' : 'Eliminar servicio'}
                </button>
              </div>
            </form>

            <div className={styles.photosSection}>
              <div className={styles.photosHeader}>
                <h3 className={styles.photosTitle}>Fotos del servicio</h3>
                <p className={styles.photosHint}>
                  Se convierten a WebP automáticamente para que la web no pese de más.
                </p>
              </div>

              <input
                ref={fileInputRef}
                className={styles.fileInput}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFiles}
                disabled={uploading}
              />

              <button
                type="button"
                className={styles.uploadButton}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <Loader2 className={styles.spinner} size={18} aria-hidden="true" />
                ) : (
                  <ImagePlus size={18} aria-hidden="true" />
                )}
                {uploading ? 'Convirtiendo y subiendo…' : 'Subir fotos'}
              </button>

              {selectedFotos.length === 0 ? (
                <p className={styles.photosEmpty}>
                  Todavía no hay fotos para este servicio.
                </p>
              ) : (
                <ul className={styles.photoGrid}>
                  {selectedFotos.map((foto, index) => (
                    <li key={foto.id} className={styles.photoCard}>
                      <img
                        className={styles.photo}
                        src={foto.url_foto}
                        alt=""
                        loading="lazy"
                      />

                      {foto.destacada && (
                        <span className={styles.photoBadge}>
                          <Star size={12} aria-hidden="true" />
                          Portada
                        </span>
                      )}

                      <div className={styles.photoActions}>
                        <button
                          type="button"
                          onClick={() => handleMove(foto, -1)}
                          disabled={index === 0}
                          aria-label="Mover la foto antes"
                          title="Mover antes"
                        >
                          <ArrowLeft size={15} aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMove(foto, 1)}
                          disabled={index === selectedFotos.length - 1}
                          aria-label="Mover la foto después"
                          title="Mover después"
                        >
                          <ArrowRight size={15} aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleTogglePrincipal(foto)}
                          className={foto.destacada ? styles.isPrincipal : ''}
                          aria-label={
                            foto.destacada
                              ? 'Quitar de portada'
                              : 'Usar como portada del servicio'
                          }
                          title="Portada"
                        >
                          <Star size={15} aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteFoto(foto)}
                          className={styles.isDanger}
                          aria-label="Eliminar la foto"
                          title="Eliminar"
                        >
                          <Trash2 size={15} aria-hidden="true" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        ) : (
          <section className={styles.placeholder} aria-label="Detalle del servicio">
            <p>
              Elegí un servicio para editar su precio, descripción y fotos, o creá uno nuevo.
            </p>
            {servicios.length === 0 && (
              <p className={styles.editorHint}>
                Si la lista está vacía, ejecutá la migración del catálogo en Supabase.
              </p>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

export default Servicios;
